'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import Link from 'next/link';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import { prefillCreatePage } from '@/lib/createPagePrefill';

const BRAND_VOICE_KEY = 'floai_brand_voice_sample';

type ToneDimension = { label: string; value: string; score: number };
type AnalysisResult = { overallScore: number; dimensions: ToneDimension[]; suggestions: string[] };

const TONE_TO_QUERY: Record<string, string> = {
  formal: 'professional',
  casual: 'casual',
  friendly: 'friendly',
  neutral: 'balanced',
};

async function analyzeText(text: string): Promise<AnalysisResult> {
  const res = await fetch('/api/tools/voice-tone-analyzer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || 'Analysis failed');
  return { overallScore: data.overallScore, dimensions: data.dimensions || [], suggestions: data.suggestions || [] };
}

export default function VoiceToneAnalyzerPage() {
  const router = useRouter();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [brandResult, setBrandResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [brandSample, setBrandSample] = useState('');
  const [brandDraft, setBrandDraft] = useState('');

  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('voiceTonePrefill') : null;
      if (!raw) return;
      sessionStorage.removeItem('voiceTonePrefill');
      const text = String(raw);
      if (text) setInput(text);
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      const saved = typeof window !== 'undefined' ? localStorage.getItem(BRAND_VOICE_KEY) : null;
      if (saved) setBrandSample(saved);
    } catch (_) {}
  }, []);

  const handleAnalyze = async () => {
    setError(null);
    setResult(null);
    setBrandResult(null);
    setLoading(true);
    try {
      const contentPromise = analyzeText(input.trim());
      const brandPromise = brandSample.trim() ? analyzeText(brandSample.trim()) : Promise.resolve(null);
      const [contentData, brandData] = await Promise.all([contentPromise, brandPromise]);
      setResult(contentData);
      setBrandResult(brandData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUseInCreate = () => {
    if (input.trim()) {
      prefillCreatePage({ content: input.trim() });
      router.push('/dashboard/create');
    }
  };

  const handleOpenBrandModal = () => {
    setBrandDraft(brandSample);
    setBrandModalOpen(true);
  };

  const handleSaveBrand = () => {
    try {
      const trimmed = brandDraft.trim();
      if (trimmed) localStorage.setItem(BRAND_VOICE_KEY, trimmed);
      setBrandSample(trimmed);
      setBrandModalOpen(false);
    } catch (_) {}
  };

  const handleClearBrand = () => {
    try {
      localStorage.removeItem(BRAND_VOICE_KEY);
      setBrandSample('');
      setBrandDraft('');
      setBrandResult(null);
      setBrandModalOpen(false);
    } catch (_) {}
  };

  const handleGenerateMoreInThisVoice = () => {
    if (!result) return;
    const dims = result.dimensions || [];
    const top = dims.length ? dims.reduce((a, b) => (a.score >= b.score ? a : b), dims[0]) : null;
    const toneParam = top ? TONE_TO_QUERY[top.value] || top.value : 'balanced';
    const voiceSummary = result.suggestions?.[0] ? encodeURIComponent(result.suggestions[0].slice(0, 80)) : '';
    const params = new URLSearchParams({ tone: toneParam });
    if (voiceSummary) params.set('voiceSummary', voiceSummary);
    router.push(`/tools/ai-content-creation?${params.toString()}`);
  };

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 12, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton component={Link} href="/tools" aria-label="Back to Tools"><ArrowBackIcon /></IconButton>
        <Icon icon={getToolIconForPath('/tools/voice-tone-analyzer')} width={34} height={34} />
        <Typography variant="h4">Voice & Tone Analyzer</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paste or type content to analyze voice and tone for brand consistency.
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={4}
        maxRows={12}
        placeholder="Paste a caption or post to analyze voice and tone."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        sx={{ mb: 2 }}
        inputProps={{ 'aria-label': 'Content to analyze' }}
      />

      <Button
        type="button"
        variant="outlined"
        fullWidth
        onClick={handleOpenBrandModal}
        sx={{ minHeight: 44, mb: 1 }}
      >
        Compare to my brand
      </Button>
      <Button
        type="button"
        variant="contained"
        fullWidth
        onClick={handleAnalyze}
        disabled={loading || !input.trim()}
        sx={{ minHeight: 44, mb: 2 }}
        aria-label="Analyze voice and tone"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Analyze'}
      </Button>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {result && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Overall tone score</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                role="img"
                aria-label={`Tone score ${result.overallScore} out of 100`}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: result.overallScore >= 70 ? 'success.light' : result.overallScore >= 40 ? 'warning.light' : 'grey.300',
                  color: 'grey.900',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                }}
              >
                {result.overallScore}
              </Box>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={result.overallScore} sx={{ height: 8, borderRadius: 1 }} />
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tone dimensions</Typography>
            <Stack spacing={1.5}>
              {result.dimensions.map((d) => (
                <Box key={d.value} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>{d.label}</Typography>
                  <LinearProgress variant="determinate" value={d.score} sx={{ flex: 1, height: 8, borderRadius: 1 }} />
                  <Typography variant="body2" fontWeight={600}>{d.score}%</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Suggestions</Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.5}>
              {result.suggestions.map((s, i) => (
                <Typography key={i} component="li" variant="body2">{s}</Typography>
              ))}
            </Stack>
          </Paper>

          {brandResult && (
            <Paper sx={{ p: 2 }} component="section" aria-labelledby="brand-comparison-heading">
              <Typography id="brand-comparison-heading" variant="subtitle2" color="text.secondary" gutterBottom>Brand comparison</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Your content:</Typography>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.light', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{result.overallScore}</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Brand sample:</Typography>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'secondary.light', color: 'secondary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{brandResult.overallScore}</Box>
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {Math.abs(result.overallScore - brandResult.overallScore) <= 15 ? 'Tone is close to your brand.' : result.overallScore > brandResult.overallScore + 15 ? 'More intense than your brand sample.' : 'Softer than your brand sample.'}
              </Typography>
            </Paper>
          )}

          <Button
            type="button"
            variant="outlined"
            fullWidth
            onClick={handleUseInCreate}
            sx={{ minHeight: 44 }}
          >
            Use in Create
          </Button>
          <Button
            type="button"
            variant="outlined"
            fullWidth
            onClick={handleGenerateMoreInThisVoice}
            sx={{ minHeight: 44 }}
          >
            Generate more in this voice
          </Button>
        </Stack>
      )}

      <Dialog open={brandModalOpen} onClose={() => setBrandModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Compare to my brand</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste a short sample of your brand voice (e.g. a caption or post you consider on-brand). We’ll compare analyzed content to it.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder="Paste your brand voice sample…"
            value={brandDraft}
            onChange={(e) => setBrandDraft(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleClearBrand} color="inherit">Clear</Button>
          <Button type="button" onClick={() => setBrandModalOpen(false)}>Cancel</Button>
          <Button type="button" variant="contained" onClick={handleSaveBrand}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
